/**
 * x3dh.js — X3DH key agreement (Signal spec) per stabilire il segreto iniziale
 * SK del Double Ratchet. ⚠️ MODULO ISOLATO + DA AUDIRE.
 *
 * Identity key (IK) = la coppia box X25519 statica già derivata dalla mnemonica.
 * Il destinatario pubblica un bundle: IK + Signed PreKey (SPK) + One-Time PreKey
 * (OPK, opzionale). L'iniziatore genera una EK effimera e calcola:
 *   DH1=DH(IK_A, SPK_B)  DH2=DH(EK_A, IK_B)  DH3=DH(EK_A, SPK_B)  [DH4=DH(EK_A, OPK_B)]
 *   SK = HKDF(0xFF*32 || DH1||DH2||DH3[||DH4])
 * Il destinatario ricalcola SK con le proprie chiavi private. Poi:
 *   iniziatore → ratchet.initAlice(SK, SPK_B.pub);  destinatario → ratchet.initBob(SK, SPK_B)
 */

import sodium from 'libsodium-wrappers-sumo';

const te = (s) => new TextEncoder().encode(s);
const DH = (priv, pub) => sodium.crypto_scalarmult(priv, pub);
function concat(...arrs) {
  const len = arrs.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
}

async function x3dhKDF(km) {
  const F = new Uint8Array(32).fill(0xff); // prefisso anti-attacco da spec
  const ikm = concat(F, km);
  const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: te('NullumX3DHV1') }, baseKey, 256,
  );
  return new Uint8Array(bits); // 32 byte = SK
}

/** Lato INIZIATORE. IK_A/EK_A = {pub,priv}; le *_pub del destinatario. OPK opzionale. */
export async function x3dhInitiator(IK_A, EK_A, IK_B_pub, SPK_B_pub, OPK_B_pub = null) {
  await sodium.ready;
  const dh1 = DH(IK_A.priv, SPK_B_pub);
  const dh2 = DH(EK_A.priv, IK_B_pub);
  const dh3 = DH(EK_A.priv, SPK_B_pub);
  const km = OPK_B_pub ? concat(dh1, dh2, dh3, DH(EK_A.priv, OPK_B_pub)) : concat(dh1, dh2, dh3);
  return x3dhKDF(km);
}

/**
 * Mescola il segreto X3DH (classico) col segreto del KEM post-quantum (ML-KEM)
 * → SK ibrido. Così "harvest now, decrypt later" è inefficace: serve ROMPERE
 * sia X25519 sia ML-KEM per ricavare SK. @returns {Uint8Array} 32 byte.
 */
export async function mixPq(skX3dh, ssPq) {
  const ikm = concat(skX3dh, ssPq);
  const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: te('NullumHybridV1') }, baseKey, 256,
  );
  return new Uint8Array(bits);
}

/** Lato DESTINATARIO. IK_B/SPK_B/OPK_B = {pub,priv}; le *_pub dell'iniziatore. */
export async function x3dhResponder(IK_B, SPK_B, OPK_B, IK_A_pub, EK_A_pub) {
  await sodium.ready;
  const dh1 = DH(SPK_B.priv, IK_A_pub);
  const dh2 = DH(IK_B.priv, EK_A_pub);
  const dh3 = DH(SPK_B.priv, EK_A_pub);
  const km = OPK_B ? concat(dh1, dh2, dh3, DH(OPK_B.priv, EK_A_pub)) : concat(dh1, dh2, dh3);
  return x3dhKDF(km);
}
